import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, type Theme } from '@mui/material/styles'
import {
  CHECKLIST_DOCUMENT,
  KG_PER_LB,
  LITRES_PER_USG,
  formatKias,
  formatUnit,
  unitLabel,
  type UnitSystem,
  groupCruise,
  groupSpeeds,
  POH_DOCUMENT,
  isScanOnly,
  daysUntilExpiry,
  expiryStatus,
  PIN_REVEAL_SECONDS,
  formatMinutes,
  sortDocuments,
  type AircraftCruiseRow,
  type AircraftDocument,
  type AircraftPowerSetting,
  type AircraftSpeed,
  type ExpiryStatus,
  type SpeedCategory,
} from '@jupi/shared'
import { UnitSystemToggle, formatDate, useClubToday, useUnitSystem } from '@jupi/ui'
import { fetchFuelCardPin, type Aircraft, type AircraftFlight } from '../api/queries'

export const scanUrl = (registration: string, documentType: string) =>
  `/api/aircraft/${registration}/documents/${documentType}/scan`

// What a pilot reads first: the two figures every logbook leads with.
// Fuel on board after the last logged flight: a bar against the usable
// fuel, `~` where it is calculated, amber where the log may have a gap.
export function FuelGauge({ fuel }: { fuel: NonNullable<Aircraft['fuel']> }) {
  const { t } = useTranslation()
  const share = fuel.usableLitres ? Math.min(fuel.litres / fuel.usableLitres, 1) : null
  return (
    <Tooltip title={t(fuel.estimated ? 'aircraft.fuelOnBoardEstimated' : 'aircraft.fuelOnBoard')}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: fuel.uncertain ? 'warning.main' : 'text.secondary' }}>
        <Icon icon="mdi:gauge" width={18} />
        {share !== null ? (
          <LinearProgress
            variant="determinate"
            value={share * 100}
            color={fuel.uncertain ? 'warning' : share < 0.25 ? 'error' : 'primary'}
            sx={{ flex: 1, height: 6, borderRadius: 3 }}
          />
        ) : null}
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {fuel.estimated ? '~' : ''}
          {Math.round(fuel.litres)} l
        </Typography>
      </Stack>
    </Tooltip>
  )
}

export function AircraftStats({ totals }: { totals: { airframeMinutes: number; landings: number } | null }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" spacing={4}>
      <Stat label={t('aircraft.totalHours')} value={totals ? formatMinutes(totals.airframeMinutes) : t('aircraft.noTotals')} />
      <Stat label={t('aircraft.totalLandings')} value={totals ? String(totals.landings) : t('aircraft.noTotals')} />
    </Stack>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="h4" component="p" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

// The handbook and the checklist get a card of their own, one big tap from
// the top of the page: on the ramp, in a hurry, they must not be rows among
// nine.
export function PohCard({ registration, documents }: { registration: string; documents: AircraftDocument[] | undefined }) {
  const { t } = useTranslation()
  const has = (type: string) => documents?.some((d) => d.documentType === type && d.hasScan) ?? false
  const buttons = [
    { type: POH_DOCUMENT, key: 'poh', icon: 'mdi:book-open-page-variant' },
    { type: CHECKLIST_DOCUMENT, key: 'checklist', icon: 'mdi:format-list-checks' },
  ].filter((b) => has(b.type))
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: documents ? 1.5 : 0 }}>
          {t('aircraft.poh.title')}
        </Typography>
        {buttons.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: `repeat(${buttons.length}, minmax(0, 1fr))` } }}>
            {buttons.map((b) => (
              <Button
                key={b.type}
                fullWidth
                size="large"
                variant="contained"
                component="a"
                href={scanUrl(registration, b.type)}
                target="_blank"
                rel="noopener"
                startIcon={<Icon icon={b.icon} />}
                endIcon={<Icon icon="mdi:open-in-new" />}
                sx={{ minHeight: 56, justifyContent: 'center', fontSize: '1.05rem' }}
              >
                {t(`aircraft.${b.key}.open`)}
              </Button>
            ))}
          </Box>
        ) : documents ? (
          <Typography variant="body2" color="text.secondary">
            {t('aircraft.poh.missing')}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )
}

// A translucent wash of the status colour under the normal text colour, so
// contrast holds in light and dark theme alike.
const ROW_TONE: Record<ExpiryStatus, { palette: 'success' | 'warning' | 'error' | null; icon: string }> = {
  none: { palette: null, icon: 'mdi:file-document-outline' },
  valid: { palette: 'success', icon: 'mdi:check-circle-outline' },
  expiring: { palette: 'warning', icon: 'mdi:alert-outline' },
  expired: { palette: 'error', icon: 'mdi:alert-circle-outline' },
}

// One row per paper, soonest expiry first. The state is the row's tint, an
// icon and the words ("in 12 days"), never the colour alone. Tapping a row
// with a scan opens it.
export function DocumentsCard({ registration, documents }: { registration: string; documents: AircraftDocument[] }) {
  const { t } = useTranslation()
  const today = useClubToday()
  const rows = sortDocuments(documents.filter((d) => !isScanOnly(d.documentType)))
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('aircraft.documentsTitle')}
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('aircraft.noDocuments')}
          </Typography>
        ) : (
          // Two columns on a wide screen, so a long list does not push the
          // cards beside it down the page. Soonest expiry reads row by row.
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            {rows.map((d) => {
              const status = expiryStatus(d.expiryDate, today)
              const tone = ROW_TONE[status]
              const days = d.expiryDate ? daysUntilExpiry(d.expiryDate, today) : null
              const words =
                days === null
                  ? ''
                  : days < 0
                    ? t('aircraft.docStatus.expired', { count: -days })
                    : days === 0
                      ? t('aircraft.docStatus.today')
                      : days > 60
                        ? t('aircraft.docStatus.valid')
                        : t('aircraft.docStatus.inDays', { count: days })
              const content = (
                <>
                  <Icon icon={tone.icon} width={24} style={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t(`aircraft.docType.${d.documentType}`)}
                    </Typography>
                    {words ? <Typography variant="body2">{words}</Typography> : null}
                  </Box>
                  <Typography variant="body1" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {d.expiryDate ? formatDate(`${d.expiryDate}T12:00:00Z`) : '–'}
                  </Typography>
                  {d.hasScan ? <Icon icon="mdi:file-eye-outline" width={22} style={{ flexShrink: 0 }} aria-label={t('aircraft.openScan')} /> : null}
                </>
              )
              const sx = {
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                minHeight: 48,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                color: 'text.primary',
                bgcolor: (theme: Theme) =>
                  tone.palette ? alpha(theme.palette[tone.palette].main, 0.18) : theme.palette.action.hover,
              }
              return (
                <li key={d.documentType}>
                  {d.hasScan ? (
                    <ButtonBase
                      component="a"
                      href={scanUrl(registration, d.documentType)}
                      target="_blank"
                      rel="noopener"
                      sx={{ ...sx, font: 'inherit' }}
                    >
                      {content}
                    </ButtonBase>
                  ) : (
                    <Box sx={sx}>{content}</Box>
                  )}
                </li>
              )
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export function RecentFlightsCard({ flights }: { flights: AircraftFlight[] }) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardContent>
        <Typography variant="h6">{t('aircraft.recentFlights')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('aircraft.flightsHint')}
        </Typography>
        {flights.length === 0 ? (
          <Alert severity="info" variant="outlined">
            {t('aircraft.noFlights')}
          </Alert>
        ) : (
          <Stack spacing={1} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {flights.map((f) => (
              <Box
                component="li"
                key={f.flightId}
                sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}
              >
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatDate(f.takeoffAt)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {f.departure} → {f.arrival}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMinutes(f.flightMinutes)} · {f.landings} {t('aircraft.landingsShort')}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

// The aircraft's fuel card PIN. Masked until a pilot taps it; then it is
// fetched (not kept in any cache) and shown in a dialog that closes itself
// after PIN_REVEAL_SECONDS, or sooner with the button.
export function FuelCardPin({ registration }: { registration: string }) {
  const { t } = useTranslation()
  const [pin, setPin] = useState<string | null>(null)
  const [left, setLeft] = useState(PIN_REVEAL_SECONDS)
  const [failed, setFailed] = useState(false)
  const open = pin !== null

  useEffect(() => {
    if (!open) return
    setLeft(PIN_REVEAL_SECONDS)
    const timer = window.setInterval(() => setLeft((n) => n - 1), 1000)
    return () => window.clearInterval(timer)
  }, [open])
  useEffect(() => {
    if (open && left <= 0) setPin(null)
  }, [open, left])

  const reveal = () => {
    setFailed(false)
    fetchFuelCardPin(registration).then(setPin, () => setFailed(true))
  }

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        onClick={reveal}
        startIcon={<Icon icon="mdi:credit-card-lock-outline" />}
        aria-label={t('aircraft.fuelCardPin.reveal', { registration })}
        sx={{ minHeight: 48, justifyContent: 'flex-start' }}
      >
        {t('aircraft.fuelCardPin.label')}: ••••
      </Button>
      {failed ? (
        <Typography variant="caption" color="error" role="alert">
          {t('aircraft.fuelCardPin.failed')}
        </Typography>
      ) : null}
      <Dialog open={open} onClose={() => setPin(null)} fullWidth maxWidth="xs" aria-labelledby={`pin-${registration}`}>
        <DialogTitle id={`pin-${registration}`}>{t('aircraft.fuelCardPin.title', { registration })}</DialogTitle>
        <DialogContent>
          <Typography
            variant="h3"
            component="p"
            sx={{ fontWeight: 700, letterSpacing: '0.2em', fontVariantNumeric: 'tabular-nums', textAlign: 'center', py: 2 }}
          >
            {pin}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }} aria-live="off">
            {t('aircraft.fuelCardPin.closing', { seconds: Math.max(left, 0) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPin(null)} autoFocus>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// The recommended power settings, one row per phase. Fuel consumption in the
// operating data stays the planner's single figure.
export function PowerSettingsCard({ settings }: { settings: AircraftPowerSetting[] }) {
  const { t } = useTranslation()
  const dash = '–'
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('aircraft.powerSettings.title')}
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums', '& th, & td': { py: 0.75, pr: 1.5, textAlign: 'left', whiteSpace: 'nowrap' } }}>
            <thead>
              <tr>
                <Typography component="th" variant="caption" color="text.secondary">{t('aircraft.powerSettings.phase')}</Typography>
                <Typography component="th" variant="caption" color="text.secondary">RPM</Typography>
                <Typography component="th" variant="caption" color="text.secondary">MP</Typography>
                <Typography component="th" variant="caption" color="text.secondary">%</Typography>
                <Typography component="th" variant="caption" color="text.secondary">l/h</Typography>
                <Typography component="th" variant="caption" color="text.secondary">USG/h</Typography>
                <Typography component="th" variant="caption" color="text.secondary">TAS</Typography>
              </tr>
            </thead>
            <tbody>
              {settings.map((s, i) => (
                <tr key={i}>
                  <Typography component="td" variant="body2" sx={{ fontWeight: 600 }}>{s.phase}</Typography>
                  <Typography component="td" variant="body2">{s.rpm ?? dash}</Typography>
                  <Typography component="td" variant="body2">{s.manifoldInhg ?? dash}</Typography>
                  <Typography component="td" variant="body2">{s.powerPercent ?? dash}</Typography>
                  <Typography component="td" variant="body2">
                    {s.fuelFlowUsg === null ? dash : (s.fuelFlowUsg * LITRES_PER_USG).toFixed(1)}
                  </Typography>
                  <Typography component="td" variant="body2">{s.fuelFlowUsg ?? dash}</Typography>
                  <Typography component="td" variant="body2">{s.tasKt ?? dash}</Typography>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

const NUMERIC = { fontVariantNumeric: 'tabular-nums' } as const

// The flight manual's operating speeds and cruise table. Empty until the
// club has entered them, and then the card is not shown at all.
export function FlightManualCard({ speeds, cruise }: { speeds: AircraftSpeed[]; cruise: AircraftCruiseRow[] }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  if (speeds.length === 0 && cruise.length === 0) return null
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1, mb: 0.5 }}>
          <Typography variant="h6">{t('flightManual.title')}</Typography>
          <UnitSystemToggle />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
          {t('flightManual.hint')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 3, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'start',
          }}
        >
          <SpeedsTable speeds={speeds} category="normal" system={system} />
          <SpeedsTable speeds={speeds} category="emergency" system={system} />
        </Box>
        <CruiseTable cruise={cruise} />
      </CardContent>
    </Card>
  )
}

function SpeedsTable({ speeds, category, system }: { speeds: AircraftSpeed[]; category: SpeedCategory; system: UnitSystem }) {
  const { t } = useTranslation()
  const groups = groupSpeeds(speeds, category)
  if (groups.length === 0) return <Box />

  const conditions = (s: AircraftSpeed) =>
    [
      // The manual's weights are pounds; metric shows the same weight in kg.
      s.weightLb !== null
        ? t('flightManual.weight', { weight: formatUnit('mass', s.weightLb * KG_PER_LB, system, 0), unit: unitLabel('mass', system) })
        : null,
      s.altitudeFt === null ? null : s.altitudeFt === 0 ? t('flightManual.seaLevel') : t('flightManual.feet', { ft: s.altitudeFt }),
      s.flaps === null ? null : s.flaps === 'up' ? t('flightManual.flapsUp') : s.flaps === 'down' ? t('flightManual.flapsDown') : t('flightManual.flapsDeg', { deg: s.flaps }),
    ]
      .filter(Boolean)
      .join(' · ')

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t(`flightManual.${category}`)}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>{t('flightManual.speed')}</TableCell>
            <TableCell>{t('flightManual.conditions')}</TableCell>
            <TableCell align="right" sx={{ pr: 0 }}>
              {t('flightManual.kias')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.flatMap((group) =>
            group.rows.map((s, i) => (
              <TableRow key={`${group.speedCode}-${s.weightLb}-${s.altitudeFt}-${s.flaps}`}>
                <TableCell sx={{ pl: 0, fontWeight: 600, ...(i > 0 ? { borderTop: 0 } : {}) }}>
                  {i === 0 ? t(`flightManual.speeds.${group.speedCode}`, { defaultValue: group.speedCode.replaceAll('_', ' ') }) : null}
                </TableCell>
                <TableCell sx={i > 0 ? { borderTop: 0 } : undefined}>
                  {conditions(s)}
                  {s.notes ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {s.notes}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right" sx={{ pr: 0, whiteSpace: 'nowrap', ...NUMERIC }}>
                  {formatKias(s.kiasMin, s.kiasMax)}
                </TableCell>
              </TableRow>
            )),
          )}
        </TableBody>
      </Table>
    </Box>
  )
}

function CruiseTable({ cruise }: { cruise: AircraftCruiseRow[] }) {
  const { t } = useTranslation()
  if (cruise.length === 0) return null
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2">{t('flightManual.cruise')}</Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
        {t('flightManual.cruiseHint')}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ maxWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }}>{t('flightManual.altitude')}</TableCell>
              <TableCell align="right">{t('flightManual.rpm')}</TableCell>
              <TableCell align="right">{t('flightManual.bhp')}</TableCell>
              <TableCell align="right">l/h</TableCell>
              <TableCell align="right" sx={{ pr: 0 }}>
                USG/h
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupCruise(cruise).flatMap((group) =>
              group.rows.map((row, i) => (
                <TableRow key={`${row.pressureAltitudeFt}-${row.rpm}`}>
                  <TableCell sx={{ pl: 0, fontWeight: 600, ...(i > 0 ? { borderTop: 0 } : {}), ...NUMERIC }}>
                    {i === 0 ? t('flightManual.feet', { ft: group.pressureAltitudeFt }) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ ...(i > 0 ? { borderTop: 0 } : {}), ...NUMERIC }}>
                    {row.rpm}
                  </TableCell>
                  <TableCell align="right" sx={{ ...(i > 0 ? { borderTop: 0 } : {}), ...NUMERIC }}>
                    {row.percentBhp}
                  </TableCell>
                  <TableCell align="right" sx={{ ...(i > 0 ? { borderTop: 0 } : {}), ...NUMERIC }}>
                    {(row.fuelUsgPerHour * LITRES_PER_USG).toFixed(1)}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0, ...(i > 0 ? { borderTop: 0 } : {}), ...NUMERIC }}>
                    {row.fuelUsgPerHour.toFixed(1)}
                  </TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
